import React, { useState } from 'react';
import styled from 'styled-components';
import { SelectDropdown, SearchDropdown, Checkbox, IconButtonWrapper } from '@prosolve/common-components-ui';
import { FaRegTrashAlt, FaExclamationTriangle } from 'react-icons/fa';

const ConditionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding: 10px;
  border: 1px solid #dedede;
  border-radius: 5px;
  background: #fafafa;
`

const WarningText = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  font-size: 12px;
  font-style: italic;
  color: #7e7e7e;
`

const operatorOptions = [
  { value: 'any', text: 'selected ANY of' },
  { value: 'all', text: 'selected ALL of' },
  { value: 'none', text: 'selected NONE of' }
]

export const getEligibleQuestions = (questions = []) =>
  questions.filter(({ type, answerOptions = [] }) =>
    ['checkbox', 'radio', 'multitext'].includes(type) && answerOptions.length > 0);

export default ({
  condition = {},
  questions = [],
  onChange,
  onDelete,
  isReadOnly
}) => {
  const { questionId = '', operator = 'any', optionIds = [] } = condition;

  const [questionSearchText, setQuestionSearchText] = useState('')

  const eligibleQuestions = getEligibleQuestions(questions);

  const allQuestionOptions = [
    { value: '', text: 'Select a question...' },
    ...eligibleQuestions.map(({ id, question }) => ({
      value: String(id),
      text: question || `Question ${id}`
    }))
  ];

  const questionSearch = questionSearchText.trim().toLowerCase();
  const questionOptions = questionSearch
    ? allQuestionOptions.filter(({ text }) => text.toLowerCase().includes(questionSearch))
    : allQuestionOptions;

  const selectedQuestion = eligibleQuestions.find(({ id }) => String(id) === String(questionId));

  const isStaleQuestion = Boolean(questionId) && !selectedQuestion;

  const answerOptions = selectedQuestion ? (selectedQuestion.answerOptions || []) : [];

  const staleOptionIds = selectedQuestion
    ? optionIds.filter(optionId => !answerOptions.some(({ id }) => String(id) === String(optionId)))
    : [];

  const onQuestionChanged = (newQuestionId) => {
    if (String(newQuestionId) === String(questionId)) return;
    // Options belong to the previous question — clear them
    onChange({ ...condition, questionId: newQuestionId, optionIds: [] })
  }

  const onOptionToggled = (optionId) => {
    const idStr = String(optionId);
    const updatedOptionIds = optionIds.some(id => String(id) === idStr)
      ? optionIds.filter(id => String(id) !== idStr)
      : [...optionIds, idStr];
    onChange({ ...condition, optionIds: updatedOptionIds })
  }

  return (
    <ConditionWrapper>
      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px', flexWrap: 'wrap', rowGap: '10px' }}>
        <div style={{ fontSize: '14px', color: '#565656' }}>{'On question'}</div>
        <SearchDropdown
          options={questionOptions}
          value={isStaleQuestion ? '' : String(questionId)}
          onChange={onQuestionChanged}
          onSearch={setQuestionSearchText}
          searchDelay={200}
          width='300px'
          maxHeight='200px'
          placeholder='Type to search questions...'
          suppressInputLoading={true}
          disabled={isReadOnly}
        />
        <div style={{ fontSize: '14px', color: '#565656' }}>{'the student'}</div>
        <SelectDropdown
          options={operatorOptions}
          value={operator}
          onChange={(newOperator) => {
            if (newOperator !== operator) {
              onChange({ ...condition, operator: newOperator })
            }
          }}
          width='180px'
          disabled={isReadOnly}
        />
        <div style={{ flex: 1 }} />
        {
          !isReadOnly &&
          <IconButtonWrapper onClick={onDelete} title='Remove condition'>
            <FaRegTrashAlt style={{ color: '#565656' }} />
          </IconButtonWrapper>
        }
      </div>
      {
        isStaleQuestion &&
        <WarningText>
          <FaExclamationTriangle style={{ color: '#EAC60C' }} />
          {`This condition referenced a question (id ${questionId}) that no longer exists. Select a new question.`}
        </WarningText>
      }
      {
        Boolean(answerOptions.length) &&
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px', paddingLeft: '10px' }}>
          {
            answerOptions.map(({ id, answerOption }) => (
              <Checkbox
                key={id}
                label={answerOption || `Option ${id}`}
                value={optionIds.some(optionId => String(optionId) === String(id))}
                onChange={() => {
                  if (!isReadOnly) {
                    onOptionToggled(id)
                  }
                }}
                disabled={isReadOnly}
              />
            ))
          }
        </div>
      }
      {
        Boolean(staleOptionIds.length) &&
        <WarningText>
          <FaExclamationTriangle style={{ color: '#EAC60C' }} />
          {`Selected answer option(s) no longer exist on this question: ${staleOptionIds.join(', ')}.`}
          {
            !isReadOnly &&
            <div
              style={{ color: '#0280c6', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                const validOptionIds = optionIds.filter(optionId =>
                  answerOptions.some(({ id }) => String(id) === String(optionId)));
                onChange({ ...condition, optionIds: validOptionIds })
              }}
            >
              {'Remove them'}
            </div>
          }
        </WarningText>
      }
    </ConditionWrapper>
  )
}
